import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Terminal, Code2, Database, Cloud, Wrench, BookOpen } from 'lucide-react';
import ProfileCard from '@/components/profilecard/profile-card';
import avatar from  '../components/profilecard/profile-pic-no-bg.png';

const AboutPage = () => {
  const skills = [
    { category: 'Frontend', items: ['React', 'Vue', 'Angular', 'TypeScript', 'Tailwind CSS', 'Redux', 'Electron', 'Ionic', 'React Native', 'Micro frontends', 'Modular Frontends', 'GraphQL'] },
    { category: 'Backend', items: ['Java', 'JavaScript', 'Node.js', 'Python', 'Java Spark', 'Quarkus.io', 'Vert.x', 'GraalVM', 'Spring Boot', 'FastAPI', 'Express', 'REST APIs', 'Flask', 'Pandas'] },
    { category: 'Datasources', items: ['MySql','MongoDB', 'PostgreSQL', 'Redis', 'Firebase', 'DynamoDB', 'Open Search'] },
    { category: 'DevOps', items: ['Terraform', 'Kubernetes', 'Docker', 'AWS', 'CI/CD', 'GitHub Actions', 'Gitlab CI', 'Jenkins']},
    { category: 'Tools', items: ['Git', 'VS Code', '...'] },
    { category: 'Communication Protocols', items: ['HTTP1.1', 'HTTP2', 'WebSockets', 'MQTT', 'SSE', 'gRPC', 'WebRTC', 'QuicLayer'] },
  ];

  const experience = [
    {
      title: 'Principal Software Engineer',
      company: 'SkipTheDishes Restaurants Inc.',
      period: '2023 - Present',
      description: 'Scope of work includes designing and implementing scalable software solutions.'
    },       
    {
      title: 'Technology Manager',
      company: 'SkipTheDishes Restaurants Inc.',
      period: '2020 - 2023',
      description: 'Managed a team of developers to deliver high-quality software solutions.'
    },    
    {
      title: 'Senior Full-Stack Developer',
      company: 'SkipTheDishes Restaurants Inc.',
      period: '2019 - 2020',
      description: 'Leading development of scalable web applications, mentoring junior developers, and architecting cloud-native solutions.'
    },
    {
      title: 'Full-Stack Developer',
      company: 'Alpar Tecnologia LTDA',
      period: '2017 - 2019',
      description: 'Implemented and maintained new feature for several customer projects. Most the time implemented new features for  an airmiles contractor company.'
    },
    {
      title: 'Junior Developer',
      company: 'Wisecode Development LTDA',
      period: '2015 - 2017',
      description: 'Developed responsive websites and web applications. Maintained/Developed Website, Mobile app and desktop application for a School Management platform that provided various tools for teachers, students and administrators.'
    },
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <div className="inline-block mb-6">
              <div className="w-32 h-32 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                <Terminal className="w-16 h-16 text-black" />
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="about-title">
              About Me
            </h1>

            <p className="text-lg text-zinc-400 leading-relaxed" data-testid="about-intro">
              Hi, I'm a passionate full-stack developer with over 9 years of experience
              building web applications. I love turning complex problems into simple and 
              intuitive solutions. When I'm not coding, you'll find me
              writing technical articles, contributing to open-source projects, or
              exploring the latest web technologies.
            </p>
            <div style={{ display: 'flex' , justifyContent: 'center', marginTop: '2rem' }}>
              <ProfileCard
                name="Jonathas Santos"
                title="Principal Software Engineer"
                handle="RuizJonathas"
                status="Online"
                contactText="Contact Me"
                avatarUrl={avatar}
                grainUrl='https://reactbits.dev/assets/grain.webp'
                behindGradient="Jon"
                showUserInfo={true}
                showBehindGradient={true}
                enableTilt={true}
                enableMobileTilt={false}
                onContactClick={() => location.href = '/contact'}
              />   
            </div>
            
          </div>
        </div>
      </section>
      
      {/* Certificates Section */}
      <section className="py-10" data-testid="certificates-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-12 text-center">Certificates</h2>
          </div>
      </section>      

      {/* Skills Section */}
      <section className="py-10" data-testid="skills-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center">Technical Skills</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.map((skillGroup, index) => (
              <Card key={index} className="glass border-zinc-800 p-6" data-testid={`skill-card-${index}`}>
                <div className="flex items-center mb-4">
                  {index === 0 && <Code2 className="w-5 h-5 text-emerald-400 mr-2" />}
                  {index === 1 && <Terminal className="w-5 h-5 text-cyan-400 mr-2" />}
                  {index === 2 && <Database className="w-5 h-5 text-violet-400 mr-2" />}
                  {index === 3 && <Cloud className="w-5 h-5 text-blue-400 mr-2" />}
                  {index === 4 && <Wrench className="w-5 h-5 text-orange-400 mr-2" />}
                  <h3 className="text-lg text-white font-semibold">{skillGroup.category}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {skillGroup.items.map((skill, i) => (
                    <Badge key={i} variant="secondary" className="bg-zinc-800 text-zinc-300 hover:bg-zinc-700">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 bg-zinc-950/50" data-testid="experience-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center">Experience</h2>
          
          <div className="space-y-8">
            {experience.map((job, index) => (
              <Card key={index} className="glass border-zinc-800 p-6 hover-lift" data-testid={`experience-card-${index}`}>
                <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-3">
                  <div>
                    <h3 className="text-xl font-semibold text-emerald-400">{job.title}</h3>
                    <p className="text-zinc-300">{job.company}</p>
                  </div>
                  <span className="font-code text-sm text-zinc-500 mt-2 md:mt-0">{job.period}</span>
                </div>
                <p className="text-zinc-400">{job.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20" data-testid="philosophy-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="glass border-zinc-800 p-8">
            <div className="flex items-center mb-4">
              <BookOpen className="w-6 h-6 text-emerald-400 mr-3" />
              <h2 className="text-2xl text-white font-bold">My Philosophy</h2>
            </div>
            <div className="space-y-4 text-zinc-400">
              <p>
                I believe in writing clean, maintainable code that solves real problems.
                Technology should serve people, not the other way around. Most importantly,
                I don't see a future where technology carries on without human touch.
              </p>
              <p>
                Continuous learning is at the core of my approach. The tech landscape
                evolves rapidly, and staying curious and adaptable is essential. I'm
                committed to sharing what I learn through writing and open-source contributions.
              </p>
              <p>
                Nothing great is built alone. Collaboration and community are vital in software development.
              </p>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
